import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLogout, useTotpVerify } from "@/api/hooks";
import { OtpInput } from "@/components/OtpInput";
import { Button, Field, Input, Notice } from "@/components/ui";
import { AuthLayout } from "@/layouts/AuthLayout";
import { errorMessage } from "@/lib/errors";

export function VerifyPage() {
  const { t } = useTranslation();
  const verify = useTotpVerify();
  const logout = useLogout();
  const [mode, setMode] = useState<"totp" | "recovery">("totp");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState("");

  // On success the session refetches as `active` and the route guard redirects.
  const send = (value: string) => {
    if (!verify.isPending) verify.mutate(value);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    send(mode === "totp" ? code : recovery.trim());
  };

  const switchMode = () => {
    verify.reset();
    setMode((m) => (m === "totp" ? "recovery" : "totp"));
  };

  return (
    <AuthLayout step={2}>
      <div>
        <h1 className="m-0 mb-1.5 text-[21px] font-semibold">{t("auth.verifyTitle")}</h1>
        <p className="m-0 text-muted">{mode === "totp" ? t("auth.verifyHint") : t("auth.recoveryHint")}</p>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {verify.isError && <Notice tone="blocked">{errorMessage(t, verify.error)}</Notice>}
        {mode === "totp" ? (
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={send}
            invalid={verify.isError}
            disabled={verify.isPending}
            autoFocus
          />
        ) : (
          <Field label={t("auth.recoveryLabel")}>
            {(p) => (
              <Input
                {...p}
                value={recovery}
                onChange={(e) => setRecovery(e.target.value)}
                className="font-mono tracking-wider"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
              />
            )}
          </Field>
        )}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={verify.isPending}
          disabled={mode === "totp" ? code.length !== 6 : !recovery.trim()}
        >
          {t("auth.verify")}
        </Button>
        <div className="flex flex-wrap justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={switchMode}>
            {mode === "totp" ? t("auth.useRecovery") : t("auth.useAuthenticator")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => logout.mutate()}>
            {t("auth.otherAccount")}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
